import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { usersAPI } from '../../services/api';

interface Agent {
  id: number;
  name: string;
  phoneNumber: string;
  signupOtp: string;
  signinOtp: string;
  company: string;
  city: string;
  state: string;
  status: number;
}

interface UsersState {
  users: Agent[];
  loading: boolean;
  error: string | null;
  statistics: {
    active: number;
    deactive: number;
    total: number;
  };
  statisticsLoading: boolean;
  filters: {
    search: string;
    status: string;
  };
  pagination: {
    page: number;
    items_per_page: number;
    total: number;
  };
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
  statistics: {
    active: 0,
    deactive: 0,
    total: 0,
  },
  statisticsLoading: false,
  filters: {
    search: '',
    status: '',
  },
  pagination: {
    page: 1,
    items_per_page: 10,
    total: 0,
  },
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params?: {
    page?: number;
    items_per_page?: number;
    search?: string;
    "where[roleId]"?: number;
    "where[status]"?: number;
    // "where[city]"?: string;
    "where[groupId]"?: string;
    sort?: string;
  }) => {
    try {
      const response = await usersAPI.getUsers(params);
      let users = [];
      let pagination = {
        page: 1,
        items_per_page: 10,
        total: 0,
      };
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        users = response.data.data;
        const payload = response.data.payload || {};
        pagination = {
          page: payload.page || 1,
          items_per_page: payload.items_per_page || payload.limit || 10,
          total: payload.total || users.length,
        };
      } else if (Array.isArray(response.data)) {
        users = response.data;
        pagination = {
          page: 1,
          items_per_page: response.data.length,
          total: response.data.length,
        };
      } else if (response.data && Array.isArray(response.data.users)) {
        users = response.data.users;
        pagination.total = users.length;
      }
      return { items: users, pagination };
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your internet connection and try again.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to fetch users. Please try again.');
      }
    }
  }
);

export const fetchUserStatistics = createAsyncThunk(
  'users/fetchUserStatistics',
  async () => {
    try {
      // Single API now returns all stats; call it and parse defensively
      const statResponse = await usersAPI.getstatics();

      const parseStatsFromResponse = (response: any) => {
        if (!response) return { active: 0, deactive: 0, total: 0 };
        const data = response.data ?? response;
        // legacy: array of items
        if (Array.isArray(data)) return { active: data.length, deactive: 0, total: data.length };
        // legacy nested payload: { data: [...] }
        if (data && Array.isArray(data.data)) return { active: data.data.length, deactive: 0, total: data.data.length };
        // legacy alternative: { users: [...] }
        if (data && Array.isArray(data.users)) return { active: data.users.length, deactive: 0, total: data.users.length };
        // new shape: object with numeric fields
        const active = typeof data.active === 'number' ? data.active : (typeof data.activeCount === 'number' ? data.activeCount : 0);
        const deactive = typeof data.inactive === 'number' ? data.inactive : (typeof data.deactive === 'number' ? data.deactive : 0);
        const total = typeof data.total === 'number' ? data.total : (typeof data.count === 'number' ? data.count : (active + deactive || 0));
        return { active: active || 0, deactive: deactive || 0, total: total || (active + deactive) };
      };

      const { active: activeCount, deactive: deactiveCount, total: totalCount } = parseStatsFromResponse(statResponse);

      const result = {
        active: activeCount,
        deactive: deactiveCount,
        total: totalCount,
      };

      return result;
    } catch (error: any) {
      // Provide more specific error messages
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your internet connection and try again.');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to fetch user statistics. Please try again.');
      }
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.items;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      })
      .addCase(fetchUserStatistics.pending, (state) => {
        state.statisticsLoading = true;
      })
      .addCase(fetchUserStatistics.fulfilled, (state, action) => {
        state.statisticsLoading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchUserStatistics.rejected, (state) => {
        state.statisticsLoading = false;
      });
  },
});

export default usersSlice.reducer; 